
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function RegulationsPage() {
  const regulations = [
    {
      title: 'Business Registration',
      icon: 'ri-file-text-line',
      steps: [
        'Choose your business structure (LLC, Corporation, Sole Proprietorship)',
        'Register with Texas Secretary of State',
        'Obtain Federal Employer Identification Number (EIN)',
        'Register for Texas state taxes',
      ],
      link: '/regulations/business-registration',
    },
    {
      title: 'Local Permits & Licenses',
      icon: 'ri-shield-check-line',
      steps: [
        'Apply for City of El Paso Business License',
        'Obtain Certificate of Occupancy',
        'Get health permits (if applicable)',
        'Secure professional or industry-specific licenses',
      ],
      link: '/regulations/local-permits-licenses',
    },
    {
      title: 'Tax Requirements',
      icon: 'ri-money-dollar-circle-line',
      steps: [
        'Register for Texas Sales and Use Tax permit',
        'Set up federal tax accounts with IRS',
        'Understand local property tax obligations',
        'File quarterly and annual tax returns',
      ],
      link: '/regulations/tax-requirements',
    },
    {
      title: 'Zoning & Compliance',
      icon: 'ri-map-pin-line',
      steps: [
        'Verify zoning regulations for your location',
        'Ensure compliance with building codes',
        'Obtain signage permits',
        'Meet environmental regulations (if applicable)',
      ],
      link: '/regulations/zoning-compliance',
    },
  ];

  const resources = [
    {
      title: 'Texas Secretary of State',
      description: 'Business entity registration and filing',
      link: '/regulations/texas-secretary-of-state',
    },
    {
      title: 'City of El Paso Business License',
      description: 'Local business licensing information',
      link: '/regulations/city-of-el-paso-business-license',
    },
    {
      title: 'Texas Comptroller',
      description: 'State tax permits and compliance information',
      link: '/regulations/texas-comptroller',
    },
    {
      title: 'IRS Small Business Resources',
      description: 'Federal tax information and EIN registration',
      link: '/regulations/irs-small-business-resources',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="El Paso Business Regulations - Permits, Licenses & Compliance Guide"
        description="Navigate El Paso business regulations with ease. Complete guide to Texas business registration, local permits, licenses, tax requirements, and zoning compliance for El Paso entrepreneurs."
        canonical="/regulations"
        keywords="El Paso business license, Texas business registration, El Paso permits, business compliance Texas, zoning regulations El Paso, Texas tax requirements"
      />
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <header className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">Local Regulations</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Navigate El Paso's business regulations with confidence. Here's everything you need to know about permits, licenses, and compliance requirements.
            </p>
          </header>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            {regulations.map((reg, index) => (
              <Link
                key={index}
                to={reg.link}
                className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-xl transition-all duration-300 block"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${reg.icon} text-2xl text-white`}></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 pt-2">{reg.title}</h2>
                </div>
                <ul className="space-y-4">
                  {reg.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-green-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </section>

          <section className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-3xl p-12">
            <h2 className="text-4xl font-bold text-white mb-8 text-center">Official Resources</h2>
            <nav className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {resources.map((resource, index) => (
                <Link
                  key={index}
                  to={resource.link}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all cursor-pointer"
                >
                  <h3 className="text-xl font-bold text-white mb-2">{resource.title}</h3>
                  <p className="text-sm text-white/80 leading-relaxed mb-4">{resource.description}</p>
                  <div className="flex items-center space-x-2 text-[#FFE8D6] font-semibold text-sm">
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              ))}
            </nav>
          </section>

          <aside className="mt-16 bg-[#FFE8D6] rounded-2xl p-8 flex items-start space-x-4">
            <div className="w-12 h-12 bg-[#D2691E] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-2xl text-white"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Important Note</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Regulations and requirements may vary based on your specific business type and location. We recommend consulting with a local business attorney or the City of El Paso Business Services department for personalized guidance.
              </p>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
